import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Typography, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CatalogIcon from '@material-ui/icons/FolderOpen';
import ServicesIcon from '@material-ui/icons/LaptopMac';
import TeamsIcon from '@material-ui/icons/PeopleAlt';
import ApisIcon from '@material-ui/icons/Public';
import RepositoriesIcon from '@material-ui/icons/Storage';

import { useCatalogCounts } from './hooks/useCatalogCounts';
import { useManagedTeamWithChildren } from './hooks/useManagedTeamWithChildren';

const GILROY = '"Gilroy", ui-sans-serif, system-ui, sans-serif';
const PROXIMA = '"Proxima Nova", ui-sans-serif, system-ui, sans-serif';

const useStyles = makeStyles(theme => ({
  catalogQuickAccessContainer: {
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxShadow: theme.shadows[1],
    overflow: 'hidden',
  },
  catalogQuickAccessHeader: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `10px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.primary,
  },
  catalogHeaderIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  catalogQuickAccessItemsContainer: {
    backgroundColor: theme.palette.background.paper,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  catalogQuickAccessItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:not(:last-child)': {
      borderRight: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down('md')]: {
        borderRight: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      [theme.breakpoints.down('sm')]: {
        borderRight: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
    '&:nth-child(4n)': {
      [theme.breakpoints.up('md')]: {
        borderRight: 'none',
      },
    },
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  catalogItemIcon: {
    marginRight: theme.spacing(1.5),
    color: theme.palette.primary.main,
    fontSize: '2rem',
  },
  catalogHeaderTitle: {
    fontFamily: GILROY,
    fontSize: '1rem',
    fontWeight: 700,
  },
  catalogItemLabel: {
    fontFamily: PROXIMA,
    fontWeight: 500,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  catalogItemCount: {
    fontFamily: PROXIMA,
    marginLeft: theme.spacing(1),
    padding: '2px 8px',
    borderRadius: 12,
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    fontWeight: 500,
  },
  chevronIcon: {
    color: theme.palette.action.disabled,
    fontSize: '1.2rem',
  },
}));

interface QuickCatalogAccessProps {
  onMyTeamsClick?: () => void;
}

export const QuickCatalogAccess = ({
  onMyTeamsClick,
}: QuickCatalogAccessProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { catalogCounts, catalogCountsLoading } = useCatalogCounts();
  const { managedTeamInfo } = useManagedTeamWithChildren();

  const handleMyTeamsClick = () => {
    if (onMyTeamsClick) {
      onMyTeamsClick();
    } else {
      navigate('/catalog-list?filters[kind]=group&filters[user]=all');
    }
  };

  const handleMyRepositoriesClick = () => {
    if (managedTeamInfo && managedTeamInfo.allTeamRefs.length > 0) {
      const ownerParams = managedTeamInfo.allTeamRefs
        .map(ref => `filters[owners]=${encodeURIComponent(ref)}`)
        .join('&');
      navigate(
        `/catalog-list?filters[kind]=repository&${ownerParams}&filters[user]=all`,
      );
    } else {
      navigate('/catalog-list?filters[kind]=repository&filters[user]=owned');
    }
  };

  const handleMyServicesClick = () => {
    navigate(
      '/services?filters[kind]=Component&filters[type]=service&filters[user]=owned',
    );
  };

  const handleMyApisClick = () => {
    navigate('/api-docs?filters[kind]=api&filters[user]=owned');
  };

  return (
    <Box className={classes.catalogQuickAccessContainer}>
      <Box className={classes.catalogQuickAccessHeader}>
        <CatalogIcon className={classes.catalogHeaderIcon} />
        <Typography variant="subtitle1" className={classes.catalogHeaderTitle}>
          Catalog quick access
        </Typography>
      </Box>

      <Box className={classes.catalogQuickAccessItemsContainer}>
        <Grid container>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              className={classes.catalogQuickAccessItem}
              onClick={handleMyTeamsClick}
            >
              <Box display="flex" alignItems="center">
                <TeamsIcon className={classes.catalogItemIcon} />
                <Typography className={classes.catalogItemLabel}>
                  My teams
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Typography className={classes.catalogItemCount}>
                  {catalogCountsLoading ? '...' : catalogCounts?.teams || 0}
                </Typography>
                <ChevronRightIcon className={classes.chevronIcon} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              className={classes.catalogQuickAccessItem}
              onClick={handleMyRepositoriesClick}
            >
              <Box display="flex" alignItems="center">
                <RepositoriesIcon className={classes.catalogItemIcon} />
                <Typography className={classes.catalogItemLabel}>
                  My Repositories
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Typography className={classes.catalogItemCount}>
                  {catalogCountsLoading
                    ? '...'
                    : catalogCounts?.repositories || 0}
                </Typography>
                <ChevronRightIcon className={classes.chevronIcon} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              className={classes.catalogQuickAccessItem}
              onClick={handleMyServicesClick}
            >
              <Box display="flex" alignItems="center">
                <ServicesIcon className={classes.catalogItemIcon} />
                <Typography className={classes.catalogItemLabel}>
                  My services
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Typography className={classes.catalogItemCount}>
                  {catalogCountsLoading ? '...' : catalogCounts?.services || 0}
                </Typography>
                <ChevronRightIcon className={classes.chevronIcon} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              className={classes.catalogQuickAccessItem}
              onClick={handleMyApisClick}
            >
              <Box display="flex" alignItems="center">
                <ApisIcon className={classes.catalogItemIcon} />
                <Typography className={classes.catalogItemLabel}>
                  My APIs
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Typography className={classes.catalogItemCount}>
                  {catalogCountsLoading ? '...' : catalogCounts?.apis || 0}
                </Typography>
                <ChevronRightIcon className={classes.chevronIcon} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
